import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { useParams } from 'react-router-dom';
import { useRLTemplateAndSupplierFieldConfig } from 'queries/estimates';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import { Layout } from 'components/Form';
import Button from 'components/Button';
import {
  useBidTypeChoices,
  useRepChoices,
  useRLColorChoices,
} from 'queries/company';
import { useJobSupplierChoices, useRLTemplateChoices } from 'queries/job';
import useAuth from 'hooks/useAuth';

interface IFormValues {
  rep: number;
  bid_type: string;
  supplier: number;
  supplier_location?: number;
  srs_job_account?: number;
  template: number;
  color: string;
}

type AppProps = {
  onSubmit: (...args: any) => any;
};

const CreateEstimateFromDrawing: React.FC<AppProps> = ({ onSubmit }) => {
  const { userInfo } = useAuth();
  const { jobId = '' } = useParams();
  const { data: config, isLoading } =
    useRLTemplateAndSupplierFieldConfig(jobId);

  const [supplier, setSupplier] = useState<any>(null);
  const [supplierLocation, setSupplierLocation] = useState<any>(null);
  const [srsJobAccount, setSrsJobAccount] = useState<any>(null);

  const schema = yup.object(
    processSchema(
      {
        template: yup.number(),
        rep: yup.number(),
        supplier: yup.number(),
        color: yup.string(),
        bid_type: yup.string(),
        supplier_location: yup.number().nullable().optional(),
        srs_job_account: yup.number().nullable().optional(),
      },
      config,
      ['supplier_location', 'srs_job_account']
    )
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    setValue,
  } = useForm<IFormValues>({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (config) {
      const defaultValues = processInitialValues(config);
      reset(defaultValues);
    }
  }, [config]);

  const _onSubmit: SubmitHandler<IFormValues> = async (formData) => {
    if (supplier?.integrated) {
      if (!supplierLocation) {
        setError('supplier_location', {
          message: 'Please select a supplier location',
        });
        return;
      }

      if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
        setError('srs_job_account', {
          message: 'Please select a SRS job account',
        });
        return;
      }
    }

    const payload = {
      ...formData,
      srs_job_account: srsJobAccount
        ? {
            jobAccountNumber: srsJobAccount['value'],
            jobAccountName: srsJobAccount['name'],
          }
        : undefined,
      job: jobId,
    };
    onSubmit(payload);
  };

  const fields = useMemo(() => {
    let _fields: any[] = [
      {
        name: 'template',
        type: FieldType.AsyncSelect,
        label: 'Select Template',
        placeholder: 'Select Template',
        error: errors.template?.message,
        control,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        optionsPromise: () => useRLTemplateChoices(jobId),
      },
      {
        name: 'supplier',
        type: FieldType.AsyncSelect,
        label: 'Select Supplier',
        placeholder: 'Select Supplier',
        error: errors.supplier?.message,
        orderByLabel: false,
        forceDropdown: true,
        control,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        optionsPromise: () => useJobSupplierChoices(jobId),
        postSelect: (selected: any) => {
          console.log('selected', selected);
          setSupplier(selected);
          if (selected.integrated && selected.locations?.length > 0) {
            const selectedLocation = selected.locations[0];
            setSupplierLocation(selectedLocation);
            setValue('supplier_location', selectedLocation?.value);
            if (selectedLocation.job_accounts?.length > 0) {
              const selectedJobAccount = selectedLocation.job_accounts[0];
              setSrsJobAccount(selectedJobAccount);
              setValue('srs_job_account', selectedJobAccount?.value);
            }
          } else {
            setSupplierLocation(null);
            setSrsJobAccount(null);
            setValue('supplier_location', undefined);
            setValue('srs_job_account', undefined);
          }
        },
      },
    ];

    if (supplier?.integrated && supplier?.locations?.length > 1) {
      _fields.push({
        name: 'supplier_location',
        type: FieldType.Select,
        label: 'Select Supplier Location',
        options: supplier.locations,
        error: errors.supplier_location?.message,
        control,
        postSelect: (selected: any) => {
          setSupplierLocation(selected);
          if (selected.job_accounts?.length > 0) {
            const selectedJobAccount = selected.job_accounts[0];
            setSrsJobAccount(selectedJobAccount);
            setValue('srs_job_account', selectedJobAccount?.value);
          } else {
            setSrsJobAccount(null);
            setValue('srs_job_account', undefined);
          }
        },
      });
    }

    if (supplier?.integrated && supplierLocation?.job_accounts?.length > 1) {
      _fields.push({
        name: 'srs_job_account',
        type: FieldType.Select,
        label: 'Select SRS Job Account',
        options: supplierLocation.job_accounts,
        error: errors.srs_job_account?.message,
        control,
        postSelect: (selected: any) => {
          setSrsJobAccount(selected);
        },
      });
    }

    _fields = [
      ..._fields,
      {
        name: 'color',
        type: FieldType.AsyncSelect,
        label: 'Select Color',
        placeholder: 'Select Color',
        error: errors.color?.message,
        forceDropdown: true,
        control,
        optionsPromise: useRLColorChoices,
      },
      {
        name: 'rep',
        type: FieldType.AsyncSelect,
        label: 'Select Rep',
        placeholder: 'Select rep',
        error: errors.rep?.message,
        control,
        optionsPromise: useRepChoices,
      },
      {
        name: 'bid_type',
        type: FieldType.AsyncSelect,
        label: 'Select Bid Type',
        placeholder: 'Select Bid Type',
        error: errors.bid_type?.message,
        control,
        optionsPromise: useBidTypeChoices,
        orderByLabel: false,
      },
    ];

    return _fields;
  }, [
    errors,
    control,
    userInfo,
    supplier,
    supplierLocation,
    srsJobAccount,
    jobId,
    config,
  ]);

  // Handle default value assignments in useEffect
  useEffect(() => {
    if (supplier?.integrated && supplier?.locations?.length === 1) {
      setSupplierLocation(supplier.locations[0]);
      setValue('supplier_location', supplier.locations[0]?.value);
    }
  }, [supplier]);

  useEffect(() => {
    if (supplier?.integrated && supplierLocation?.job_accounts?.length === 1) {
      setSrsJobAccount(supplierLocation.job_accounts[0]);
      setValue('srs_job_account', supplierLocation.job_accounts[0]?.value);
    }
  }, [supplierLocation, supplier]);

  if (isLoading) {
    return <div className="center h-40">Fetching form config...</div>;
  }

  return (
    <div>
      <form onSubmit={handleSubmit(_onSubmit)}>
        <div className="mt-4">
          <Layout
            fields={processFields(fields, config, [
              'supplier_location',
              'srs_job_account',
            ])}
          />
        </div>
        <Button label="Create Estimate" className="mt-8" />
      </form>
    </div>
  );
};

export default CreateEstimateFromDrawing;
